import { Injectable } from '@angular/core';
import { ClientDevice, ClientDeviceInfo } from '../interfaces/client-device-info.interface';
import { LocalStorage } from '../storage.class';
import { ApiService, PaginatedResponse } from './api/api.service';

// User-Agent Client Hints are Chromium-only and not part of the TypeScript DOM lib.
interface UADataBrand {
    brand: string;
    version: string;
}

interface UAData {
    brands: UADataBrand[];
    mobile: boolean;
    platform: string;
    getHighEntropyValues(
        hints: string[]
    ): Promise<{ model?: string; platformVersion?: string; fullVersionList?: UADataBrand[] }>;
}

/**
 * POC: registers which device, OS, browser and screen the user works with, for device statistics.
 */
@Injectable({
    providedIn: 'root',
})
export class ClientDeviceService {
    private registeredUserId: number | null | undefined;

    constructor(private apiService: ApiService) {}

    /**
     * Registers once per page load and user. Failures are ignored so the app is never affected.
     */
    register() {
        const userId = LocalStorage.getUser()?.id ?? null;
        if (userId === this.registeredUserId) {
            return;
        }
        this.registeredUserId = userId;

        this.collect().then(info =>
            this.apiService.postCall$('logging/client-device', info).subscribe({ error: () => {} })
        );
    }

    getList(page: number) {
        return this.apiService.getCallData$<PaginatedResponse<ClientDevice>>('codalt/client-devices', { page });
    }

    private async collect(): Promise<ClientDeviceInfo> {
        const user_agent = navigator.userAgent;
        const userAgentData = (navigator as Navigator & { userAgentData?: UAData }).userAgentData;
        const hints = await userAgentData
            ?.getHighEntropyValues(['model', 'platformVersion', 'fullVersionList'])
            .catch(() => undefined);
        const platform = userAgentData?.platform;

        let device_type: ClientDeviceInfo['device_type'] = 'unknown';
        let device_vendor: string | undefined;
        let device_model: string | undefined;
        let os_name: string | undefined;
        let os_version: string | undefined;

        // iPadOS Safari presents itself as a Mac by default, only touch support gives it away.
        const isIPad =
            user_agent.includes('iPad') || (user_agent.includes('Macintosh') && navigator.maxTouchPoints > 1);
        const isIPhone = user_agent.includes('iPhone') || user_agent.includes('iPod');

        if (isIPhone || isIPad) {
            device_type = isIPhone ? 'mobile' : 'tablet';
            device_vendor = 'Apple';
            device_model = isIPhone ? 'iPhone' : 'iPad';
            os_name = 'iOS';
            // A Mac-presenting iPad reports a frozen macOS version; Safari's version follows iPadOS instead.
            os_version =
                user_agent.match(/OS (\d+(?:_\d+)*) like Mac OS X/)?.[1].replace(/_/g, '.') ??
                user_agent.match(/Version\/([\d.]+)/)?.[1];
        } else if (platform === 'Android' || user_agent.includes('Android')) {
            device_type = (userAgentData ? userAgentData.mobile : user_agent.includes('Mobile')) ? 'mobile' : 'tablet';
            os_name = 'Android';
            os_version = hints?.platformVersion || user_agent.match(/Android ([\d.]+)/)?.[1];
            // Reduced User-Agents replace the model with "K", Firefox puts "Mobile" or "Tablet" there.
            const uaModel = user_agent.match(/Android [\d.]+; ([^;)]+)/)?.[1].replace(/ Build\/.*/, '');
            device_model = hints?.model || (['K', 'Mobile', 'Tablet'].includes(uaModel) ? undefined : uaModel);
            device_vendor = device_model?.startsWith('SM-')
                ? 'Samsung'
                : device_model?.startsWith('Pixel')
                  ? 'Google'
                  : undefined;
        } else if (platform === 'Windows' || user_agent.includes('Windows')) {
            device_type = 'desktop';
            os_name = 'Windows';
            // The User-Agent reports NT 10.0 for both Windows 10 and 11, platformVersion 13+ means Windows 11.
            const platformMajor = parseInt(hints?.platformVersion ?? '', 10);
            os_version =
                platformMajor >= 13 ? '11' : platformMajor > 0 ? '10' : user_agent.match(/Windows (NT [\d.]+)/)?.[1];
        } else if (platform === 'macOS' || user_agent.includes('Macintosh')) {
            device_type = 'desktop';
            device_vendor = 'Apple';
            os_name = 'macOS';
            // The User-Agent version is frozen at 10.15.7, only Client Hints report the real one.
            os_version =
                hints?.platformVersion || user_agent.match(/Mac OS X (\d+(?:[_.]\d+)*)/)?.[1].replace(/_/g, '.');
        } else if (platform === 'Chrome OS' || user_agent.includes('CrOS')) {
            device_type = 'desktop';
            os_name = 'ChromeOS';
            os_version = hints?.platformVersion;
        } else if (platform === 'Linux' || user_agent.includes('Linux')) {
            device_type = 'desktop';
            os_name = 'Linux';
        } else if (userAgentData?.mobile || user_agent.includes('Mobi')) {
            device_type = 'mobile';
        }

        const browser = this.detectBrowser(user_agent, hints?.fullVersionList ?? userAgentData?.brands);

        return {
            device_type,
            device_vendor,
            device_model,
            os_name,
            os_version,
            browser_name: browser.name,
            browser_version: browser.version,
            screen_width: screen.width,
            screen_height: screen.height,
            viewport_width: window.innerWidth,
            viewport_height: window.innerHeight,
            pixel_ratio: Math.round(window.devicePixelRatio * 100) / 100,
            touch_points: navigator.maxTouchPoints,
            cpu_threads: navigator.hardwareConcurrency,
            language: navigator.language,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            user_agent,
        };
    }

    private detectBrowser(ua: string, brands?: UADataBrand[]): { name?: string; version?: string } {
        // Skip GREASE brands ("Not A;Brand" variants) and the generic Chromium engine brand.
        const brand = brands?.find(b => !/Not.?A.?Brand|Chromium/i.test(b.brand));
        if (brand) {
            const names: Record<string, string> = { 'Google Chrome': 'Chrome', 'Microsoft Edge': 'Edge' };
            return { name: names[brand.brand] ?? brand.brand, version: brand.version };
        }

        // Order matters: Edge, Opera and Samsung Internet also contain "Chrome", and Chrome also contains "Safari".
        const patterns: [string, RegExp][] = [
            ['Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/],
            ['Opera', /OPR\/([\d.]+)/],
            ['Samsung Internet', /SamsungBrowser\/([\d.]+)/],
            ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
            ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
            ['Safari', /Version\/([\d.]+).*Safari/],
        ];
        for (const [name, pattern] of patterns) {
            const version = ua.match(pattern)?.[1];
            if (version) {
                return { name, version };
            }
        }

        return {};
    }
}
